import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Save, X, History, Eye } from 'lucide-react';
import DashboardHeader from './DashboardHeader';

import DateFilter, { allDateFilter } from './DateFilter';
import { usePagedQuery, dateRangeParams } from './usePagedQuery';
import { Pagination, SearchBox } from './TableControls';
const API = process.env.REACT_APP_BACKEND_API_URL;

const FIELDS = [
    {
        key: 'name',
        label: 'Name',
        required: true,
        width: '4%',
        bodyStyle: { fontWeight: 600, color: '#0f172a' }
    },
    {
        key: 'phone',
        label: 'Phone Number',
        required: true,
        width: '8%',
        bodyStyle: { whiteSpace: 'nowrap' }
    },
    {
        key: 'email',
        label: 'Email',
        width: '12%',
        bodyStyle: { overflowWrap: 'anywhere' }
    },
    {
        key: 'primary_skills',
        label: 'Primary Skills',
        multiline: true,
        width: '14%',
        // bodyStyle: { whiteSpace: 'pre-wrap', lineHeight: 1.5 }
    },
    {
        key: 'secondary_skills',
        label: 'Secondary Skills',
        multiline: true,
        width: '14%',
        // bodyStyle: { whiteSpace: 'pre-wrap', lineHeight: 1.5 }
    },
    {
        key: 'certificates',
        label: 'Certificates',
        multiline: true,
        width: '14%',
        // bodyStyle: { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }
    },
    {
        key: 'availability',
        label: 'Availability',
        width: '7%',
        bodyStyle: { fontWeight: 500, color: '#475569' }
    }
];
const blank = () => ({ name: '', phone: '', email: '', primary_skills: '', secondary_skills: '', certificates: '', availability: '' });

const pad = (n) => String(n).padStart(2, '0');
// DD-MM-YYYY HH:mm (local time) — same display format as the rest of the app.
const formatStamp = (value) => {
    const d = value ? new Date(value) : null;
    if (!d || isNaN(d.getTime())) return '-';
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const validate = (f) => {
    if (!f.name.trim()) return 'Name is required.';
    const phone = f.phone.trim();
    const digits = phone.replace(/\D/g, '');
    if (!phone) return 'Phone Number is required.';
    if (!/^[0-9+()\-.\s]+$/.test(phone) || digits.length < 7 || digits.length > 15) return 'Enter a valid Phone Number (7–15 digits).';
    if (f.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return 'Enter a valid Email address.';
    return '';
};

const inputStyle = { width: '100%', padding: '6px 8px', borderRadius: '5px', border: '1px solid #cbd5e1', fontSize: '12px', boxSizing: 'border-box', fontFamily: 'inherit' };
const btn = {
    display: 'inline-flex', alignItems: 'center', gap: '5px', background: '#fff', border: '1px solid #e2e8f0',
    padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, color: '#334155', cursor: 'pointer'
};
const backTo = { admin: '/admin', teamLead: '/team-lead', recruiter: '/recruiter' };

// Named Freelancer Details — shared by Admin, Team Lead and Recruiter.
// Independent of Candidate Details: own records, own change history.
const NamedFreelancerDetails = ({ user, onLogout }) => {
    const navigate = useNavigate();
    const [notice, setNotice] = useState('');
    const [search, setSearch] = useState('');
    const [availFilter, setAvailFilter] = useState('');
    const [createdByFilter, setCreatedByFilter] = useState('');
    const [createdFilter, setCreatedFilter] = useState(allDateFilter);
    const [editingId, setEditingId] = useState(null);   // row id, or 'new'
    const [draft, setDraft] = useState(blank());
    const [rowError, setRowError] = useState('');
    const [saving, setSaving] = useState(false);
    const [historyFor, setHistoryFor] = useState(null); // freelancer row
    const [viewFor, setViewFor] = useState(null);
    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    // Server-side paging: search / availability / created-by / created-date applied by the API, newest first.
    const params = useMemo(() => ({
        search: search.trim(), availability: availFilter, created_by: createdByFilter,
        ...dateRangeParams(createdFilter, 'created')
    }), [search, availFilter, createdByFilter, createdFilter]);
    const table = usePagedQuery(`${API}/api/named-freelancers`, params);
    const rows = table.rows;
    const loading = table.loading;
    const error = table.error || '';
    const load = table.refetch;
    const options = (table.extras && table.extras.options) || { availability: [], created_by: [] };

    const flash = (m) => { setNotice(m); setTimeout(() => setNotice(''), 3500); };

    const startAdd = () => { setEditingId('new'); setDraft(blank()); setRowError(''); };
    const startEdit = (row) => {
        setEditingId(row.id);
        setDraft(FIELDS.reduce((o, f) => ({ ...o, [f.key]: row[f.key] || '' }), {}));
        setRowError('');
    };
    const cancel = () => { setEditingId(null); setRowError(''); };

    const save = async () => {
        const problem = validate(draft);
        if (problem) { setRowError(problem); return; }
        setSaving(true);
        setRowError('');
        try {
            const isNew = editingId === 'new';
            const existing = isNew ? null : rows.find(r => r.id === editingId);
            const res = await fetch(`${API}/api/named-freelancers${isNew ? '' : `/${editingId}`}`, {
                method: isNew ? 'POST' : 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(isNew ? draft : { ...draft, expected_updated_at: existing && existing.updated_at })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setRowError(data.error || 'Failed to save.');
                if (data.code === 'STALE_RECORD') load();
                return;
            }
            setEditingId(null);
            if (isNew) table.setPage(1);
            flash(isNew ? `"${data.name}" added.` : `"${data.name}" updated.`);
            load();
        } catch (e) {
            setRowError('Server connection failed.');
        } finally {
            setSaving(false);
        }
    };

    const openHistory = async (row) => {
        setHistoryFor(row);
        setLoadingHistory(true);
        setHistory([]);
        try {
            const res = await fetch(`${API}/api/named-freelancers/${row.id}/history`);
            const data = await res.json().catch(() => []);
            setHistory(res.ok && Array.isArray(data) ? data : []);
        } catch (e) {
            setHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    };

    // One entry per save: rows sharing a change_id belong together.
    const historyGroups = useMemo(() => {
        const map = new Map();
        history.forEach(h => {
            if (!map.has(h.change_id)) map.set(h.change_id, { id: h.change_id, action: h.action, by: h.changed_by, role: h.changed_by_role, at: h.changed_at, items: [] });
            map.get(h.change_id).items.push(h);
        });
        return [...map.values()];
    }, [history]);

    const visible = rows;
    const filtersActive = !!(search.trim() || availFilter || createdByFilter || createdFilter.mode !== 'All');

    const th = { textAlign: 'left', padding: '10px 8px', fontSize: '12px', color: '#64748b', fontWeight: 700, whiteSpace: 'nowrap', borderBottom: '2px solid #e2e8f0' };
    const td = { padding: '8px', fontSize: '12px', color: '#0f172a', verticalAlign: 'top', borderBottom: '1px solid #f1f5f9' };

    const renderEditCells = () => FIELDS.map(f => (
        <td
            key={f.key}
            style={{
                ...td,
                width: f.width,
                maxWidth: f.width,
                minWidth: 0
            }}
        >
            {f.multiline ? (
                <textarea
                    rows={2}
                    aria-label={f.label}
                    style={{
                        ...inputStyle,
                        width: '100%',
                        minWidth: 0,
                        maxWidth: '100%',
                        resize: 'vertical',
                        whiteSpace: 'pre-wrap'
                    }}
                    value={draft[f.key]}
                    onChange={e =>
                        setDraft({ ...draft, [f.key]: e.target.value })
                    }
                />
            ) : (
                <input
                    aria-label={f.label}
                    list={f.suggestions ? 'nf-availability' : undefined}
                    style={{
                        ...inputStyle,
                        width: '100%',
                        minWidth: 0,
                        maxWidth: '100%'
                    }}
                    value={draft[f.key]}
                    placeholder={f.required ? `${f.label} *` : f.label}
                    onChange={e =>
                        setDraft({ ...draft, [f.key]: e.target.value })
                    }
                />
            )}
        </td>
    ));

    const renderActions = (row) => (
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
            {editingId === (row ? row.id : 'new') ? (
                <>
                    <button style={{ ...btn, background: '#16a34a', color: '#fff', border: '1px solid #16a34a' }} disabled={saving} onClick={save}><Save size={12} /> {saving ? 'Saving…' : 'Save'}</button>{' '}
                    <button style={btn} disabled={saving} onClick={cancel}><X size={12} /> Cancel</button>
                    {rowError && <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px', maxWidth: '220px', whiteSpace: 'normal' }}>{rowError}</div>}
                </>
            ) : (
                <>
                    <button style={btn} onClick={() => setViewFor(row)}> <Eye size={12} /> View </button>{' '}
                    <button style={btn} disabled={editingId !== null} onClick={() => startEdit(row)}><Pencil size={12} /> Edit</button>{' '}
                    <button style={btn} onClick={() => openHistory(row)}><History size={12} /> History</button>
                </>
            )}
        </td>
    );

    return (
        <div style={{ fontFamily: 'Inter, sans-serif', background: '#f4f6f8', minHeight: '100vh', padding: '20px', boxSizing: 'border-box' }}>
            <DashboardHeader user={user} titleText="Freelancer Details" onLogout={onLogout} />

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <button onClick={() => navigate(backTo[user?.role] || '/')} style={{ ...btn, padding: '8px 14px', fontSize: '13px' }}><ArrowLeft size={14} /> Back to Dashboard</button>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Freelancer Details</h2>
                <div style={{ flex: 1 }} />
                <button onClick={startAdd} disabled={editingId !== null} style={{ ...btn, background: '#2563eb', color: '#fff', border: '1px solid #2563eb', padding: '8px 14px', fontSize: '13px' }}>
                    <Plus size={14} /> Add Freelancer
                </button>
            </div>

            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px 14px', marginBottom: '16px', display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                <SearchBox value={search} onChange={setSearch} placeholder="Search name, phone, email, skills…" minWidth="260px" />
                <select aria-label="Filter by availability" value={availFilter} onChange={(e) => setAvailFilter(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '8px 10px', fontSize: '13px' }}>
                    <option value="">All availability</option>
                    {(options.availability || []).map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <select aria-label="Filter by created by" value={createdByFilter} onChange={(e) => setCreatedByFilter(e.target.value)} style={{ ...inputStyle, width: 'auto', padding: '8px 10px', fontSize: '13px' }}>
                    <option value="">All creators</option>
                    {(options.created_by || []).map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: '#475569' }}>Created:</span>
                    <DateFilter style={{ marginBottom: 0 }} dateField="created_at" initialValue={createdFilter} onFilterChange={setCreatedFilter} />
                </div>
                <span style={{ fontSize: '12px', color: '#64748b' }}>{table.total.toLocaleString()} matching freelancers</span>
            </div>

            {notice && <div style={{ background: '#dcfce7', color: '#166534', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>{notice}</div>}
            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: '6px', fontSize: '13px', marginBottom: '12px' }}>{error}</div>}

            <datalist id="nf-availability">
                {(FIELDS.find(f => f.key === 'availability')?.suggestions || []).map(s => (
                    <option key={s} value={s} />
                ))}
            </datalist>

            <div style={{
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '8px',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflowX: 'hidden'
            }}>
                <table style={{
                    width: '100%',
                    tableLayout: 'fixed',
                    borderCollapse: 'collapse'
                }}>
                    <thead>
                        <tr>
                            <th style={{ ...th, width: '2%' }}>S.No</th>
                            {FIELDS.map(f => (
                                <th
                                    key={f.key}
                                    style={{
                                        ...th,
                                        width: f.width,
                                        maxWidth: f.width,
                                        whiteSpace: 'normal',
                                        overflowWrap: 'anywhere'
                                    }}
                                >
                                    {f.label}{f.required ? ' *' : ''}
                                </th>
                            ))}
                            <th style={{ ...th, width: '6%', whiteSpace: 'normal' }}>
                                Last Updated
                            </th>
                            <th style={{ ...th, width: '16%', whiteSpace: 'normal' }}>
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {editingId === 'new' && (
                            <tr data-testid="nf-new-row" style={{ background: '#eff6ff' }}>
                                <td style={td}>New</td>
                                {renderEditCells()}
                                <td style={td}>-</td>
                                {renderActions(null)}
                            </tr>
                        )}
                        {loading && <tr><td style={td} colSpan={FIELDS.length + 3}>Loading…</td></tr>}
                        {!loading && visible.length === 0 && editingId !== 'new' && (
                            <tr><td style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '16px' }} colSpan={FIELDS.length + 3}>{!filtersActive ? 'No freelancers added yet. Click "Add Freelancer".' : 'No freelancers match your search.'}</td></tr>
                        )}
                        {!loading && visible.map((r, i) => (
                            <tr key={r.id} data-testid={`nf-row-${r.id}`} style={editingId === r.id ? { background: '#eff6ff' } : undefined}>
                                <td style={{ ...td, color: '#64748b', fontWeight: 600 }}>{(table.page - 1) * table.pageSize + i + 1}</td>
                                {editingId === r.id
                                    ? renderEditCells()
                                    : FIELDS.map(f => (
                                        <td
                                            key={f.key}
                                            style={{
                                                ...td,
                                                width: f.width,
                                                maxWidth: f.width,
                                                whiteSpace: 'normal',
                                                overflowWrap: 'anywhere',
                                                verticalAlign: 'top',
                                                ...f.bodyStyle
                                            }}
                                        >
                                            <div
                                                style={
                                                    (f.key === 'primary_skills' ||
                                                        f.key === 'secondary_skills' ||
                                                        f.key === 'certificates')
                                                        ? {
                                                            display: '-webkit-box',
                                                            WebkitBoxOrient: 'vertical',
                                                            WebkitLineClamp: 5,
                                                            overflow: 'hidden',
                                                            whiteSpace: 'normal',
                                                            overflowWrap: 'anywhere',
                                                            wordBreak: 'normal',
                                                            lineHeight: '18px',
                                                            maxHeight: '90px'
                                                        }
                                                        : {
                                                            whiteSpace: f.multiline ? 'pre-wrap' : 'normal',
                                                            overflowWrap: 'anywhere'
                                                        }
                                                }
                                                title={r[f.key] || ''}
                                            >
                                                {r[f.key] || '-'}
                                            </div>
                                        </td>
                                    ))
                                }
                                <td style={{ ...td, color: '#64748b', fontSize: '11px' }}>{r.updated_by || '-'}<br />{formatStamp(r.updated_at)}</td>
                                {renderActions(r)}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination page={table.page} totalPages={table.totalPages} total={table.total} pageSize={table.pageSize}
                onPageChange={table.setPage} onPageSizeChange={table.setPageSize} loading={table.loading} label="freelancers" />
            {viewFor && (
                <div
                    onClick={() => setViewFor(null)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(15,23,42,0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px'
                    }}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        aria-label={`Freelancer details: ${viewFor.name}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: '#fff',
                            borderRadius: '12px',
                            padding: '20px',
                            width: '100%',
                            maxWidth: '650px',
                            maxHeight: '85vh',
                            overflowY: 'auto',
                            boxShadow: '0 20px 50px rgba(0,0,0,0.2)'
                        }}
                    >
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '18px'
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '18px',
                                color: '#0f172a'
                            }}>
                                Freelancer Details
                            </h3>

                            <button
                                onClick={() => setViewFor(null)}
                                aria-label="Close"
                                style={{
                                    ...btn,
                                    padding: '6px'
                                }}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                            gap: '12px'
                        }}>
                            {FIELDS.map((field) => (
                                <div
                                    key={field.key}
                                    style={{
                                        padding: '12px',
                                        border: '1px solid #e2e8f0',
                                        borderRadius: '8px',
                                        minWidth: 0
                                    }}
                                >
                                    <div style={{
                                        fontSize: '11px',
                                        color: '#64748b',
                                        fontWeight: 700,
                                        marginBottom: '6px'
                                    }}>
                                        {field.label}
                                    </div>

                                    <div style={{
                                        fontSize: '13px',
                                        color: '#0f172a',
                                        whiteSpace: 'pre-wrap',
                                        overflowWrap: 'anywhere',
                                        lineHeight: 1.6
                                    }}>
                                        {viewFor[field.key] || '-'}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div style={{
                            marginTop: '16px',
                            paddingTop: '12px',
                            borderTop: '1px solid #e2e8f0',
                            fontSize: '12px',
                            color: '#64748b'
                        }}>
                            <strong>Last Updated By:</strong> {viewFor.updated_by || '-'}
                            <br />
                            <strong>Last Updated:</strong> {formatStamp(viewFor.updated_at)}
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: '18px'
                        }}>
                            <button
                                onClick={() => setViewFor(null)}
                                style={{
                                    ...btn,
                                    background: '#2563eb',
                                    color: '#fff',
                                    border: '1px solid #2563eb',
                                    padding: '8px 16px'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {historyFor && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }} onClick={() => setHistoryFor(null)}>
                    <div role="dialog" aria-label="Freelancer history" onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: '10px', padding: '18px', width: '100%', maxWidth: '640px', maxHeight: '82vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <h3 style={{ margin: 0, fontSize: '15px', color: '#0f172a' }}>History — {historyFor.name}</h3>
                            <button onClick={() => setHistoryFor(null)} aria-label="Close" style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X size={16} /></button>
                        </div>
                        {loadingHistory ? <div style={{ fontSize: '13px', color: '#94a3b8' }}>Loading…</div>
                            : historyGroups.length === 0 ? <div style={{ fontSize: '13px', color: '#94a3b8' }}>No history recorded.</div>
                                : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {historyGroups.map(g => (
                                            <div key={g.id} data-testid="nf-history-group" style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 12px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '6px', fontSize: '12px' }}>
                                                    <strong style={{ color: g.action === 'Created' ? '#166534' : '#1e40af' }}>{g.action} by {g.by}{g.role ? ` (${g.role})` : ''}</strong>
                                                    <span style={{ color: '#64748b' }}>{formatStamp(g.at)}</span>
                                                </div>
                                                {g.items.map(it => (
                                                    <div key={it.id} style={{ fontSize: '12px', color: '#334155', padding: '2px 0', wordBreak: 'break-word' }}>
                                                        <strong>{it.field_name}:</strong>{' '}
                                                        {g.action === 'Created'
                                                            ? <span>{it.new_value}</span>
                                                            : <><span style={{ color: '#b91c1c', textDecoration: 'line-through' }}>{it.old_value || '(empty)'}</span> → <span style={{ color: '#166534' }}>{it.new_value || '(empty)'}</span></>}
                                                    </div>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NamedFreelancerDetails;